import React, { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { LockKeyhole } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";

export default function Login() {
  const { user, login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);

  if (user && user.role === "admin") return <Navigate to="/admin" replace />;

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await login(email.trim().toLowerCase(), password);
      toast.success("Welcome back");
      nav("/admin");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Login failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="login-page" className="max-w-md mx-auto px-6 py-24">
      <div className="mb-10">
        <div className="w-11 h-11 rounded-full bg-[#0F172A] text-white flex items-center justify-center mb-6">
          <LockKeyhole size={18}/>
        </div>
        <div className="text-xs uppercase tracking-[0.22em] text-neutral-500 mb-2">Admin only</div>
        <h1 className="font-serif text-4xl text-[#0F172A]">Sign in</h1>
        <p className="text-sm text-neutral-500 mt-2">Access the dashboard to manage books and assignments.</p>
      </div>

      <form onSubmit={onSubmit} className="space-y-5">
        <div>
          <Label htmlFor="email" className="text-xs uppercase tracking-[0.2em] text-neutral-500">Email</Label>
          <Input
            id="email"
            data-testid="login-email-input"
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="mt-2 h-11"
          />
        </div>
        <div>
          <Label htmlFor="password" className="text-xs uppercase tracking-[0.2em] text-neutral-500">Password</Label>
          <Input
            id="password"
            data-testid="login-password-input"
            type="password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="mt-2 h-11"
          />
        </div>
        <Button
          type="submit"
          data-testid="login-submit-btn"
          disabled={submitting}
          className="w-full h-11 rounded-full bg-[#0F172A] hover:bg-[#E05A47] transition-colors"
          style={{ transitionProperty: "background-color" }}
        >
          {submitting ? "Signing in..." : "Sign in"}
        </Button>
      </form>
    </div>
  );
}
